import { useQuery } from "@apollo/client";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useParams } from "react-router-dom";

import UsersPageUserDetails from "../components/UsersPageUserDetails";
import { GET_EVENT_PARTICIPANTS } from "../gql/queries/event";
import { ADMIN_EVENT_INSIGHT } from "../gql/queries/eventsCalendar";
import { Spacing } from "../types/enum";
import { type UserModel } from "../types/graphql";

enum ParticipantsStatus {
  Going = "Going",
  Invited = "Invited",
  Declined = "Declined",
}

const EventParticipantsPage: React.FC = () => {
  const { eventId } = useParams<Record<string, string>>();
  const [tabName, setTabName] = useState<ParticipantsStatus>(ParticipantsStatus.Going);

  const { data, loading } = useQuery(GET_EVENT_PARTICIPANTS, {
    fetchPolicy: "network-only",
    variables: { id: eventId },
  });
  const { data: insightData } = useQuery(ADMIN_EVENT_INSIGHT, {
    variables: { eventId },
    fetchPolicy: "cache-and-network",
    skip: !eventId,
  });

  const event = data?.getEventAdmin;
  const going: UserModel[] = event?.participants ?? [];
  const invited: UserModel[] = event?.invited ?? [];
  const declined: UserModel[] = event?.declined ?? [];
  const rows =
    tabName === ParticipantsStatus.Invited
      ? invited
      : tabName === ParticipantsStatus.Declined
        ? declined
        : going;

  const insight = insightData?.adminEventInsight;
  const capacity = insight
    ? insight.unlimited
      ? "Unlimited spots"
      : `${insight.registered}/${insight.maxParticipants} spots taken`
    : null;

  return (
    <>
      {capacity && (
        <Box display="flex" alignItems="center" gap={1} mb={Spacing.m} flexWrap="wrap">
          <Chip
            size="small"
            label={capacity}
            color={insight?.isFull ? "error" : "default"}
            variant={insight?.isFull ? "filled" : "outlined"}
          />
          <Typography variant="caption" color="textSecondary">
            {going.length} going · {invited.length} invited · {declined.length} declined
          </Typography>
        </Box>
      )}

      <Box mb={Spacing.m}>
        <Tabs
          scrollButtons="auto"
          variant="scrollable"
          textColor="secondary"
          value={tabName}
          onChange={(_, next) => setTabName(next)}
        >
          <Tab label={`Going (${going.length})`} value={ParticipantsStatus.Going} />
          <Tab label={`Invited (${invited.length})`} value={ParticipantsStatus.Invited} />
          <Tab label={`Declined (${declined.length})`} value={ParticipantsStatus.Declined} />
        </Tabs>
      </Box>

      <Card>
        {loading ? (
          <Box display="flex" justifyContent="center" p={Spacing.xxl}>
            <CircularProgress />
          </Box>
        ) : rows.length === 0 ? (
          <Box p={Spacing.l}>
            <Typography variant="body2" color="textSecondary">
              No {tabName.toLowerCase()} members.
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Name</TableCell>
                <TableCell>Community role</TableCell>
                <TableCell>E-mail</TableCell>
                <TableCell>Location</TableCell>
                <TableCell>Joined date</TableCell>
                <TableCell>Zone</TableCell>
                <TableCell align="center">Events</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((user) => (
                <UsersPageUserDetails key={user.id} user={user} />
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </>
  );
};

export default EventParticipantsPage;
